import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { UserRound, Sparkles, Plug, Layers, Check, ArrowRight, X, RefreshCw } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { StatusBadge } from '../components/billing';
import { varinymAPI } from '../lib/varinym-api';
import { authAPI } from '../lib/auth-api';
import { useAuth } from '../hooks/useAuth';

function formatCheckedAt(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function Dashboard() {
  const { customer, user } = useAuth();
  const quickStartRef = useRef<HTMLDivElement>(null);
  const [highlightQuickStart, setHighlightQuickStart] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [quickStartDismissed, setQuickStartDismissed] = useState(false);
  const hasReadQuickStartStorage = useRef(false);

  const quickStartStorageKey = customer?.id ? `varinym-quickstart-completed-${customer.id}` : null;

  // Read the persisted dismissal once per mount, before completion can write to it,
  // so the guide still shows on the load where the last step is completed.
  useEffect(() => {
    if (hasReadQuickStartStorage.current || !quickStartStorageKey) return;
    hasReadQuickStartStorage.current = true;
    setQuickStartDismissed(localStorage.getItem(quickStartStorageKey) === 'true');
  }, [quickStartStorageKey]);

  useEffect(() => {
    const handleProfileCompleted = () => {
      quickStartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightQuickStart(true);
      setShowWelcomeBanner(true);
      setTimeout(() => setHighlightQuickStart(false), 6000);
    };
    window.addEventListener('varinym:profile-completed', handleProfileCompleted);
    return () => window.removeEventListener('varinym:profile-completed', handleProfileCompleted);
  }, []);

  console.log('[Dashboard] Customer:', customer);
  console.log('[Dashboard] User:', user);
  console.log('[Dashboard] Enabled queries:', !!customer?.id);

  const queryClient = useQueryClient();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['statistics'],
    queryFn: () => {
      console.log('[Dashboard] Fetching statistics...');
      return varinymAPI.getStatistics();
    },
    enabled: !!customer?.id,
  });

  const refreshIdentityCount = useMutation({
    mutationFn: () => varinymAPI.countIdentities({ refresh: true }),
    onSuccess: (res) => {
      queryClient.setQueryData(['statistics'], (old: typeof stats) => ({
        identities: res.count || 0,
        aliases: old?.aliases ?? 0,
        aliasesCheckedAt: old?.aliasesCheckedAt,
        identitiesCheckedAt: res.checked_at,
      }));
    },
  });

  const refreshAliasCount = useMutation({
    mutationFn: () => varinymAPI.countAliases({ refresh: true }),
    onSuccess: (res) => {
      queryClient.setQueryData(['statistics'], (old: typeof stats) => ({
        identities: old?.identities ?? 0,
        identitiesCheckedAt: old?.identitiesCheckedAt,
        aliases: res.count || 0,
        aliasesCheckedAt: res.checked_at,
      }));
    },
  });

  const { data: jobsData } = useQuery({
    queryKey: ['jobs', 'recent'],
    queryFn: () => {
      console.log('[Dashboard] Fetching jobs...');
      return varinymAPI.listJobs(5, 0);
    },
    enabled: !!customer?.id,
  });

  const { data: contextsData } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(),
    enabled: !!customer?.id,
  });

  const { data: apiKeysData } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => authAPI.listApiKeys(),
    enabled: !!customer?.id,
  });

  // Calculate completion status
  // Count active contexts excluding the default context (which doesn't count towards usage)
  const activeContextsCount = contextsData?.contexts?.filter(
    s => s.active !== false && s.name.toLowerCase() !== 'default'
  ).length ?? 0;
  const hasContexts = activeContextsCount >= 1; // At least 1 user-created active context
  const hasIdentities = (stats?.identities ?? 0) > 0;
  const hasBulkJobs = (jobsData?.count ?? 0) > 0;
  const hasApiKeys = (apiKeysData?.length ?? 0) > 0;

  const quickStartSteps = [
    {
      title: 'Create a Context',
      description: 'Set up your first context configuration',
      link: '/contexts',
      completed: hasContexts,
    },
    {
      title: 'Create Identities',
      description: 'Add identities with aliases for your users',
      link: '/identities',
      completed: hasIdentities,
    },
    {
      title: 'Run Bulk Operations',
      description: 'Upload CSV files for batch processing',
      link: '/bulk',
      completed: hasBulkJobs,
    },
    {
      title: 'API Integration',
      description: 'Generate API keys for programmatic access',
      link: '/settings',
      completed: hasApiKeys,
    },
  ];

  const completedSteps = quickStartSteps.filter((step) => step.completed).length;
  const totalSteps = quickStartSteps.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;
  const allStepsCompleted = completedSteps === totalSteps;

  // Once every step is complete, remember it so the guide stays hidden on future logins.
  useEffect(() => {
    if (allStepsCompleted && quickStartStorageKey) {
      localStorage.setItem(quickStartStorageKey, 'true');
    }
  }, [allStepsCompleted, quickStartStorageKey]);

  const showQuickStart = !allStepsCompleted || !quickStartDismissed;

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Dashboard</h1>

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-muted-foreground mb-1">Total Identities</dt>
              <dd className="text-3xl font-bold text-foreground">
                {isLoading ? '...' : stats?.identities.toLocaleString()}
                {user?.subscription?.limits?.identities_max && (
                  <span className="text-sm text-muted-foreground font-normal">
                    /{user.subscription.limits.identities_max.toLocaleString()}
                  </span>
                )}
              </dd>
            </div>
            <UserRound className="h-8 w-8 text-muted-foreground" />
          </div>
          {stats?.identitiesCheckedAt && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>
                Last Counted: {formatCheckedAt(stats.identitiesCheckedAt)}
              </span>
              <button
                type="button"
                onClick={() => refreshIdentityCount.mutate()}
                disabled={refreshIdentityCount.isPending}
                aria-label="Refresh identity count"
                title="Refresh count"
                className="text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${refreshIdentityCount.isPending ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <dt className="text-sm font-medium text-muted-foreground mb-1">Total Aliases</dt>
              <dd className="text-3xl font-bold text-foreground">
                {isLoading ? '...' : stats?.aliases.toLocaleString()}
              </dd>
            </div>
            <Sparkles className="h-8 w-8 text-muted-foreground" />
          </div>
          {stats?.aliasesCheckedAt && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>
                Last Counted: {formatCheckedAt(stats.aliasesCheckedAt)}
              </span>
              <button
                type="button"
                onClick={() => refreshAliasCount.mutate()}
                disabled={refreshAliasCount.isPending}
                aria-label="Refresh alias count"
                title="Refresh count"
                className="text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${refreshAliasCount.isPending ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
        </Card>

        <Link to="/contexts" className="link-bare block">
          <Card className="hover:border-primary/50 transition-all cursor-pointer h-full">
            <div className="flex items-center justify-between">
              <div>
                <dt className="text-sm font-medium text-muted-foreground mb-1">Contexts</dt>
                <dd className="text-3xl font-bold text-foreground">
                  {activeContextsCount}
                  {user?.subscription?.limits?.contexts_max && (
                    <span className="text-sm text-muted-foreground font-normal">
                      /{user.subscription.limits.contexts_max}
                    </span>
                  )}
                </dd>
              </div>
              <Plug className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </Link>

        <Link to="/bulk" className="link-bare block">
          <Card className="hover:border-primary/50 transition-all cursor-pointer h-full">
            <div className="flex items-center justify-between">
              <div>
                <dt className="text-sm font-medium text-muted-foreground mb-1">Bulk Jobs</dt>
                <dd className="text-3xl font-bold text-foreground">
                  {jobsData?.count || 0}
                </dd>
              </div>
              <Layers className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </Link>
      </div>

      {/* Quick Start Checklist */}
      {showQuickStart && (
      <div ref={quickStartRef}>
        <Card title="Quick Start Guide" className={`mb-8 ${highlightQuickStart ? 'qs-highlight' : ''}`}>
        <div className="space-y-4">
          {showWelcomeBanner && (
            <div className="bg-muted border border-border rounded-md p-3 flex items-start justify-between gap-3">
              <div className="flex gap-2 items-start">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                <p className="text-sm text-foreground">
                  You're all set up. Work through the steps below to get productive fast — or use{' '}
                  <Link to="/help" className="text-primary hover:underline font-medium">Help</Link> in
                  the top nav any time for docs and support.
                </p>
              </div>
              <button
                onClick={() => setShowWelcomeBanner(false)}
                className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                aria-label="Dismiss"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-foreground">
                Getting Started Progress
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                {completedSteps} of {totalSteps} completed
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Checklist Items */}
          <div className="space-y-3">
            {quickStartSteps.map((step, index) => (
              <Link
                key={index}
                to={step.link}
                className="link-bare block group"
              >
                <div
                  className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-all ${
                    step.completed
                      ? 'border-success/30 bg-success/5'
                      : 'border-border bg-background hover:border-primary/50 hover:bg-accent/50'
                  }`}
                >
                  {/* Checkbox/Checkmark */}
                  <div className="flex-shrink-0 mt-0.5">
                    {step.completed ? (
                      <div className="h-6 w-6 rounded-full bg-success flex items-center justify-center">
                        <Check className="h-4 w-4 text-success-foreground" />
                      </div>
                    ) : (
                      <div className="h-6 w-6 rounded-full border-2 border-muted-foreground/30 group-hover:border-primary/50 transition-colors" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`text-sm font-semibold mb-1 ${
                        step.completed
                          ? 'text-success'
                          : 'text-foreground group-hover:text-primary'
                      } transition-colors`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0">
                    <ArrowRight
                      className={`h-5 w-5 ${
                        step.completed
                          ? 'text-success'
                          : 'text-muted-foreground group-hover:text-primary'
                      } transition-colors`}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Completion Message */}
          {allStepsCompleted && (
            <div className="mt-6 bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 text-success-foreground" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">
                    Great job! You're all set up!
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    You've completed all the quick start steps. Check out our{' '}
                    <Link to="/help" className="text-primary hover:underline font-medium">
                      Help & Documentation
                    </Link>{' '}
                    to learn more advanced features.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        </Card>
      </div>
      )}

      {/* Recent Bulk Jobs */}
      <Card title="Recent Bulk Jobs">
        {jobsData?.jobs && jobsData.jobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Job ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Operation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobsData.jobs.map((job) => (
                  <tr key={job.job_id} className="hover:bg-accent/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                      <Link to={`/bulk/jobs/${job.job_id}`} className="text-primary hover:underline">
                        {job.job_id.slice(0, 8)}...
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {job.operation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge type="job" value={job.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {job.processed_items} / {job.total_items}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(job.created_at * 1000).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>No bulk jobs yet</p>
            <Link to="/bulk" className="link-bare">
              <Button className="mt-4" size="sm">
                Create First Job
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
